import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { toast } from "sonner";
import type { Result } from "#/api";
import { ResultStatus } from "#/enum";
import { t } from "@/locales/i18n";
import userStore from "@/store/userStore";
import { TokenStorage } from "@/utils/token-storage";

function resolveApiBaseUrl(): string {
	// 优先使用环境变量配置的 API 地址
	const envUrl = import.meta.env?.VITE_APP_API_BASE_URL;
	if (envUrl && envUrl.trim() !== "") {
		return envUrl;
	}

	// 兜底：根据环境判断
	return import.meta.env?.PROD ? "https://api.fomo100.io" : "https://marsapi.greenacup.com";
}

const axiosInstance = axios.create({
	baseURL: resolveApiBaseUrl(),
	timeout: 50000,
	headers: { "Content-Type": "application/json;charset=utf-8", platform: "fomodapp" },
});

axiosInstance.interceptors.request.use(
	async (config) => {
		try {
			// 从 localforage 获取访问令牌
			const accessToken = await TokenStorage.getAccessToken();
			if (accessToken) {
				config.headers.accessToken = `${accessToken}`;
			}
		} catch (error) {
			console.error("Failed to get access token for request:", error);
			// 如果获取token失败，继续发送请求但不添加Authorization头
		}
		return config;
	},
	(error) => Promise.reject(error),
);

axiosInstance.interceptors.response.use(
	(res: AxiosResponse<Result<any> | any>) => {
		const payload: any = res.data;
		if (!payload) throw new Error(t("sys.api.apiRequestFailed"));

		// 兼容通用Result结构 {status, data, message}
		if (typeof payload.status !== "undefined") {
			if (payload.status === ResultStatus.SUCCESS) {
				return payload.data;
			}
			throw new Error(payload.message || t("sys.api.apiRequestFailed"));
		}

		// 兼容Mars接口结构 {code, data, msg}
		if (typeof payload.code !== "undefined") {
			if (payload.code === 0) {
				return payload; // 返回完整对象，调用方可使用 code/data/msg
			}

			// 处理 token 失效 (code: 10100)
			if (payload.code === 10100) {
				console.log("🔒 Token失效 (code: 10100)，清空所有状态...");

				// 1. 清空 localforage 中的所有 token 和用户信息
				TokenStorage.clearAll().catch((error) => {
					console.error("Failed to clear token storage:", error);
				});

				// 2. 清空全局 store 状态
				userStore.getState().actions.clearUserInfoAndToken();
			}

			throw new Error(payload.msg || t("sys.api.apiRequestFailed"));
		}

		// 其他情况直接返回原始数据
		return payload;
	},
	async (error: AxiosError<Result | any>) => {
		const { response, message } = error || {};
		const errMsg = response?.data?.message || response?.data?.msg || message || t("sys.api.errorMessage");
		toast.error(errMsg, { position: "top-center" });
		if (response?.status === 401) {
			// 清除存储的token和用户信息
			await TokenStorage.clearAll();
			userStore.getState().actions.clearUserInfoAndToken();
		}
		return Promise.reject(error);
	},
);

class APIClient {
	get<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "GET" });
	}
	post<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "POST" });
	}
	put<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "PUT" });
	}
	delete<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return this.request<T>({ ...config, method: "DELETE" });
	}
	request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
		return axiosInstance.request<any, T>(config);
	}
}

export default new APIClient();
